const DB_NAME = 'bw_offline_routes_cache';
const DB_VERSION = 2;
const SNAPSHOT_STORE = 'route_snapshots';
const MY_ORDERS_PROFILE_STORE = 'my_orders_profiles';
const MY_ORDERS_LIST_STORE = 'my_orders_lists';
const LAST_USER_KEY = 'offline_routes_last_user_id';

export const OFFLINE_ROUTES_CACHE_UPDATED = 'offline-routes-cache-updated';
export const OFFLINE_MY_ORDERS_CACHE_UPDATED = 'offline-my-orders-cache-updated';

const hasIndexedDb = () => (
  typeof window !== 'undefined'
  && typeof window.indexedDB !== 'undefined'
);

const normalizeUserId = (userId) => {
  const numericId = Number(userId);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
};

const normalizeStage = (stage) => String(stage || '').trim();

const normalizeMyOrdersTab = (tab) => (
  tab === 'available' || tab === 'archive' ? tab : 'my'
);

const buildMyOrdersListKey = (userId, stage, tab) => (
  `${userId}:${normalizeStage(stage)}:${normalizeMyOrdersTab(tab)}`
);

const openOfflineRoutesDb = () => new Promise((resolve, reject) => {
  if (!hasIndexedDb()) {
    reject(new Error('IndexedDB is not available'));
    return;
  }

  const request = window.indexedDB.open(DB_NAME, DB_VERSION);

  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) {
      db.createObjectStore(SNAPSHOT_STORE, { keyPath: 'userId' });
    }
    if (!db.objectStoreNames.contains(MY_ORDERS_PROFILE_STORE)) {
      db.createObjectStore(MY_ORDERS_PROFILE_STORE, { keyPath: 'userId' });
    }
    if (!db.objectStoreNames.contains(MY_ORDERS_LIST_STORE)) {
      db.createObjectStore(MY_ORDERS_LIST_STORE, { keyPath: 'cacheKey' });
    }
  };

  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Failed to open offline routes cache'));
});

const withStore = async (storeName, mode, callback) => {
  const db = await openOfflineRoutesDb();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);
      let callbackResult;

      transaction.oncomplete = () => resolve(callbackResult);
      transaction.onerror = () => reject(transaction.error || new Error('Offline routes transaction failed'));
      transaction.onabort = () => reject(transaction.error || new Error('Offline routes transaction aborted'));

      callbackResult = callback(store, resolve, reject);
    });
  } finally {
    db.close();
  }
};

export const parseOfflineCacheDate = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) return null;

  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(rawValue);
  const normalizedValue = hasTimezone ? rawValue : `${rawValue}Z`;
  const date = new Date(normalizedValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatOfflineCacheDateTime = (value, options = {}) => {
  const date = parseOfflineCacheDate(value);
  if (!date) return '';

  const { includeYear = true } = options;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    ...(includeYear ? { year: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

export const toOfflineRoutesSnapshot = (payload) => {
  const userId = normalizeUserId(payload?.user_id ?? payload?.userId);
  if (!userId) return null;

  return {
    userId,
    schemaVersion: payload?.schema_version || payload?.schemaVersion || 0,
    revision: payload?.revision || '',
    ordersRevision: payload?.orders_revision || payload?.ordersRevision || '',
    tasksRevision: payload?.tasks_revision || payload?.tasksRevision || '',
    expensesRevision: payload?.expenses_revision || payload?.expensesRevision || '',
    preferences: payload?.preferences || null,
    orderLists: payload?.order_lists || payload?.orderLists || [],
    orderDetails: payload?.order_details || payload?.orderDetails || [],
    taskDetails: payload?.task_details || payload?.taskDetails || [],
    stageRoles: payload?.stage_roles || payload?.stageRoles || [],
    userType: payload?.user_type || payload?.userType || '',
    worksWithTasks: Boolean(payload?.works_with_tasks ?? payload?.worksWithTasks),
    expensesData: payload?.expenses_data || payload?.expensesData || null,
    shellBuild: payload?.shellBuild || '',
    bytes: payload?.bytes || 0,
    syncedAt: payload?.synced_at || payload?.syncedAt || new Date().toISOString(),
    ttlHours: Number(payload?.ttl_hours ?? payload?.ttlHours ?? payload?.offline_data_ttl_hours ?? 24),
    windowStart: payload?.window_start || payload?.windowStart || '',
    windowEnd: payload?.window_end || payload?.windowEnd || '',
    items: Array.isArray(payload?.items) ? payload.items : [],
  };
};

export const mergeOfflineRoutesSnapshot = (savedSnapshot, delta) => {
  if (!savedSnapshot || delta?.is_delta !== true) {
    return toOfflineRoutesSnapshot(delta);
  }

  const orderMap = new Map();
  if (Array.isArray(savedSnapshot.orderDetails)) {
    for (const order of savedSnapshot.orderDetails) {
      if (order?.id) orderMap.set(Number(order.id), order);
    }
  }

  if (Array.isArray(delta.deleted_order_ids)) {
    for (const deletedId of delta.deleted_order_ids) {
      orderMap.delete(Number(deletedId));
    }
  }

  if (Array.isArray(delta.order_details)) {
    for (const order of delta.order_details) {
      if (order?.id) orderMap.set(Number(order.id), order);
    }
  }

  const mergedOrderDetails = Array.from(orderMap.values());

  let mergedTaskDetails = savedSnapshot.taskDetails || [];
  if (!delta.tasks_unchanged && Array.isArray(delta.task_details)) {
    mergedTaskDetails = delta.task_details;
  }

  let mergedExpensesData = savedSnapshot.expensesData || null;
  if (!delta.expenses_unchanged && delta.expenses_data) {
    mergedExpensesData = delta.expenses_data;
  }

  const mergedPayload = {
    user_id: delta.user_id || savedSnapshot.userId,
    schema_version: 1,
    revision: delta.revision || savedSnapshot.revision,
    orders_revision: delta.orders_revision || savedSnapshot.ordersRevision,
    tasks_revision: delta.tasks_revision || savedSnapshot.tasksRevision,
    expenses_revision: delta.expenses_revision || savedSnapshot.expensesRevision,
    preferences: delta.preferences || savedSnapshot.preferences,
    order_lists: delta.order_lists || savedSnapshot.orderLists,
    order_details: mergedOrderDetails,
    task_details: mergedTaskDetails,
    stage_roles: delta.stage_roles || savedSnapshot.stageRoles,
    user_type: delta.user_type || savedSnapshot.userType,
    works_with_tasks: delta.works_with_tasks ?? savedSnapshot.worksWithTasks,
    expenses_data: mergedExpensesData,
    shellBuild: delta.shellBuild || savedSnapshot.shellBuild,
    bytes: delta.bytes || savedSnapshot.bytes,
    synced_at: delta.synced_at || new Date().toISOString(),
    ttl_hours: delta.ttl_hours ?? savedSnapshot.ttlHours ?? 24,
    window_start: delta.window_start || savedSnapshot.windowStart,
    window_end: delta.window_end || savedSnapshot.windowEnd,
    items: delta.items || savedSnapshot.items,
  };

  return toOfflineRoutesSnapshot(mergedPayload);
};

export const readOfflineExpensesData = async (userId = null) => {
  const snapshot = await readOfflineRoutesSnapshot(userId);
  if (!snapshot || snapshot.schemaVersion !== 1) return null;
  return snapshot.expensesData || null;
};

export const saveOfflineRoutesSnapshot = async (payload, { signal } = {}) => {
  const snapshot = toOfflineRoutesSnapshot(payload);
  if (!snapshot) return null;

  await withStore(SNAPSHOT_STORE, 'readwrite', (store) => {
    if (signal?.aborted) {
      store.transaction.abort();
      return;
    }
    const abort = () => { try { store.transaction.abort(); } catch { /* Already committed. */ } };
    signal?.addEventListener('abort', abort, { once: true });
    store.transaction.addEventListener('complete', () => signal?.removeEventListener('abort', abort));
    store.transaction.addEventListener('abort', () => signal?.removeEventListener('abort', abort));
    store.put(snapshot);
  });

  localStorage.setItem(LAST_USER_KEY, String(snapshot.userId));
  window.dispatchEvent(new CustomEvent(OFFLINE_ROUTES_CACHE_UPDATED, { detail: snapshot }));
  return snapshot;
};

export const isOfflineSnapshotExpired = (snapshot) => {
  if (!snapshot) return false;
  const ttlHours = Number(snapshot.ttlHours ?? snapshot.offlineDataTtlHours ?? 24);
  if (!ttlHours || ttlHours <= 0) return false;
  const syncedTime = new Date(snapshot.syncedAt || 0).getTime();
  if (!syncedTime || isNaN(syncedTime)) return false;
  return Date.now() > syncedTime + ttlHours * 60 * 60 * 1000;
};

export const readOfflineRoutesSnapshot = async (userId = null, { allowExpired = false } = {}) => {
  const normalizedUserId = normalizeUserId(userId ?? localStorage.getItem('user_id') ?? localStorage.getItem(LAST_USER_KEY));
  if (!normalizedUserId) return null;

  const snapshot = await withStore(SNAPSHOT_STORE, 'readonly', (store, resolve, reject) => {
    const request = store.get(normalizedUserId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error || new Error('Failed to read offline routes cache'));
  });

  if (!snapshot) return null;

  if (!allowExpired && isOfflineSnapshotExpired(snapshot)) {
    clearAllOfflineRoutesCache().catch((err) => {
      console.warn('Не удалось очистить просроченный офлайн-кэш:', err);
    });
    window.dispatchEvent(new CustomEvent('offline-cache-expired', {
      detail: { userId: normalizedUserId, expiredAt: new Date().toISOString() }
    }));
    return null;
  }

  return snapshot;
};

export const readOfflineRouteItem = async (entityType, entityId, userId = null) => {
  const snapshot = await readOfflineRoutesSnapshot(userId);
  if (!snapshot) return null;

  const normalizedEntityType = entityType === 'task' ? 'task' : 'order';
  const normalizedEntityId = String(entityId || '').trim();
  if (!normalizedEntityId) return null;

  const item = (snapshot.items || []).find((entry) => {
    const entryType = entry?.entity_type === 'task' ? 'task' : 'order';
    return entryType === normalizedEntityType && String(entry?.order_id || '') === normalizedEntityId;
  });

  const details = normalizedEntityType === 'task' ? snapshot.taskDetails : snapshot.orderDetails;
  const detail = (details || []).find((entry) => String(entry.id) === normalizedEntityId);
  return item || detail ? { item: item || { order_id: detail.id, assigned_to: detail.assigned_to }, detail, snapshot } : null;
};

export const saveOfflineMyOrdersProfile = async (payload) => {
  const userId = normalizeUserId(payload?.user_id ?? payload?.userId);
  if (!userId) return null;

  const stageRolesSource = Array.isArray(payload?.stage_roles)
    ? payload.stage_roles
    : (Array.isArray(payload?.stageRoles) ? payload.stageRoles : []);
  const profile = {
    userId,
    stageRoles: stageRolesSource.map((stage) => normalizeStage(stage)).filter(Boolean),
    syncedAt: payload?.synced_at || payload?.syncedAt || new Date().toISOString(),
  };

  await withStore(MY_ORDERS_PROFILE_STORE, 'readwrite', (store) => {
    store.put(profile);
  });

  localStorage.setItem(LAST_USER_KEY, String(profile.userId));
  window.dispatchEvent(new CustomEvent(OFFLINE_MY_ORDERS_CACHE_UPDATED, { detail: profile }));
  return profile;
};

export const readOfflineMyOrdersProfile = async (userId = null) => {
  const normalizedUserId = normalizeUserId(userId ?? localStorage.getItem('user_id') ?? localStorage.getItem(LAST_USER_KEY));
  if (!normalizedUserId) return null;

  const pack = await readOfflineRoutesSnapshot(normalizedUserId);
  if (pack?.schemaVersion === 1) return { userId: normalizedUserId, stageRoles: pack.stageRoles, syncedAt: pack.syncedAt };

  return withStore(MY_ORDERS_PROFILE_STORE, 'readonly', (store, resolve, reject) => {
    const request = store.get(normalizedUserId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error || new Error('Failed to read offline my orders profile'));
  });
};

export const saveOfflineMyOrdersList = async (payload) => {
  const userId = normalizeUserId(payload?.user_id ?? payload?.userId);
  const stage = normalizeStage(payload?.stage);
  const tab = normalizeMyOrdersTab(payload?.tab);
  if (!userId || !stage || tab === 'archive') return null;

  const snapshot = {
    cacheKey: buildMyOrdersListKey(userId, stage, tab),
    userId,
    stage,
    tab,
    orders: Array.isArray(payload?.orders) ? payload.orders : [],
    syncedAt: payload?.synced_at || payload?.syncedAt || new Date().toISOString(),
  };

  await withStore(MY_ORDERS_LIST_STORE, 'readwrite', (store) => {
    store.put(snapshot);
  });

  localStorage.setItem(LAST_USER_KEY, String(snapshot.userId));
  window.dispatchEvent(new CustomEvent(OFFLINE_MY_ORDERS_CACHE_UPDATED, { detail: snapshot }));
  return snapshot;
};

export const readOfflineMyOrdersList = async ({ userId = null, stage, tab = 'my' } = {}) => {
  const normalizedUserId = normalizeUserId(userId ?? localStorage.getItem('user_id') ?? localStorage.getItem(LAST_USER_KEY));
  const normalizedStage = normalizeStage(stage);
  if (!normalizedUserId || !normalizedStage) return null;

  const pack = await readOfflineRoutesSnapshot(normalizedUserId);
  if (pack?.schemaVersion === 1) {
    const list = pack.orderLists.find((entry) => entry.stage === normalizedStage && entry.tab === tab);
    return list ? { ...list, userId: normalizedUserId, syncedAt: pack.syncedAt } : null;
  }

  return withStore(MY_ORDERS_LIST_STORE, 'readonly', (store, resolve, reject) => {
    const request = store.get(buildMyOrdersListKey(normalizedUserId, normalizedStage, tab));
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error || new Error('Failed to read offline my orders list'));
  });
};

export const shouldUseOfflineDetailFallback = (error) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  const status = Number(error?.status || 0);
  if (status >= 500) return true;
  if (status && status < 500) {
    return false;
  }

  const message = String(error?.message || error || '').toLowerCase();
  return [
    'нет соединения',
    'без связи',
    'сервер не отвечает',
    'failed to fetch',
    'network',
    'timeout',
    'timed out',
    'fetch failed',
    'load failed',
    'ошибка сервера',
  ].some((pattern) => message.includes(pattern));
};

export const readOfflineTasks = async (scope = 'my') => {
  const snapshot = await readOfflineRoutesSnapshot();
  if (!snapshot || snapshot.schemaVersion !== 1 || !['my', 'available', 'all'].includes(scope)) return null;
  if (scope === 'available' && !['available', 'all'].includes(snapshot.preferences?.scope)) return null;
  if (scope === 'all' && snapshot.preferences?.scope !== 'all') return null;
  if (scope === 'all') {
    return [...snapshot.taskDetails].sort((a, b) => {
      const dateA = a.completed_at || a.cancelled_at || a.scheduled_date || a.created_date || '';
      const dateB = b.completed_at || b.cancelled_at || b.scheduled_date || b.created_date || '';
      return dateB.localeCompare(dateA) || (Number(b.task_number || b.id || 0) - Number(a.task_number || a.id || 0));
    });
  }
  return snapshot.taskDetails.filter((task) => scope === 'my'
    ? Number(task.assigned_to) === snapshot.userId
    : task.status === 'available' && !task.assigned_to);
};

export const clearAllOfflineRoutesCache = async () => {
  if (!hasIndexedDb()) return;
  try {
    await Promise.all([
      withStore(SNAPSHOT_STORE, 'readwrite', (store) => {
        store.clear();
      }),
      withStore(MY_ORDERS_PROFILE_STORE, 'readwrite', (store) => {
        store.clear();
      }),
      withStore(MY_ORDERS_LIST_STORE, 'readwrite', (store) => {
        store.clear();
      }),
    ]);
  } finally {
    localStorage.removeItem(LAST_USER_KEY);
  }
};
